'use client';

import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { getClientLogger } from '@/lib/logger/pino-logger.client';
import { getReferringSite, getUrl, getUTMData } from '@/tools/views';
import { IDynamicsFieldMapping, IDynamicsRequestBody, IDynamicsResponse } from '@/types/dynamicsForms';
import { useContext, useState } from 'react';
import { useDevice } from './useDevice';
import { useRequest } from './useRequest';

export const useForm = () => {
  const { context: layoutContext } = useContext(LayoutContext);
  const { env } = useEnv();

  const [submitted, setSubmitted] = useState(false);

  const logger = getClientLogger('hooks.useForm');

  const { hasError, isLoading, sendRequest } = useRequest<IDynamicsResponse, IDynamicsRequestBody>(
    '/api/forms/submit',
    { logger },
  );
  const { device } = useDevice({ metadata: true });

  const sendForm = async (data: IDynamicsRequestBody) => {
    const mappings: IDynamicsFieldMapping[] = [...(data.mappings as IDynamicsFieldMapping[])];

    const utmData = getUTMData();
    const publishedFormUrl = getUrl();

    mappings.push(
      { DataverseFieldName: 'lmrv_deviceid', DataverseFieldValue: device?.id || '' },
      { DataverseFieldName: 'lmrv_utmsource', DataverseFieldValue: utmData.utm_source || '' },
      { DataverseFieldName: 'lmrv_utmcampaign', DataverseFieldValue: utmData.utm_campaign || '' },
      { DataverseFieldName: 'lmrv_utmmedium', DataverseFieldValue: utmData.utm_medium || '' },
      { DataverseFieldName: 'lmrv_utmcontent', DataverseFieldValue: utmData.utm_content || '' },
      { DataverseFieldName: 'lmrv_utmterm', DataverseFieldValue: utmData.utm_term || '' },
      { DataverseFieldName: 'lmrv_googleclientid', DataverseFieldValue: utmData.adVendor_gclAu || '' },
      { DataverseFieldName: 'lmrv_googleanalyticsid', DataverseFieldValue: utmData.adVendor_gaId || '' },
      { DataverseFieldName: 'lmrv_metaid', DataverseFieldValue: utmData.adVendor_fbId || '' },
      { DataverseFieldName: 'lmrv_bingid', DataverseFieldValue: utmData.adVendor_bingId || '' },
      { DataverseFieldName: 'lmrv_callrailid', DataverseFieldValue: utmData.adVendor_callRailId || '' },
      { DataverseFieldName: 'lmrv_googleclickid', DataverseFieldValue: utmData.adVendor_gclId || '' },
      { DataverseFieldName: 'lmrv_msclkid', DataverseFieldValue: utmData.adVendor_msclkId || '' },
      { DataverseFieldName: 'lmrv_url', DataverseFieldValue: publishedFormUrl },
      { DataverseFieldName: 'lmrv_referringsite', DataverseFieldValue: getReferringSite() },
    );

    if (env.ENABLE_FORMS_CAPTURE_IP) {
      mappings.push(
        { DataverseFieldName: 'lmrv_ip', DataverseFieldValue: device.ip || '' },
        { DataverseFieldName: 'lmrv_useragent', DataverseFieldValue: device.userAgent || '' },
        { DataverseFieldName: 'lmrv_platform', DataverseFieldValue: device.platform || '' },
      );
    }

    if (env.ENABLE_FORMS_CAPTURE_GEO && !!layoutContext.geoLocation) {
      mappings.push({
        DataverseFieldName: 'lmrv_geocoordinates',
        DataverseFieldValue: layoutContext.geoLocation.join(','),
      });
    }

    const response = await sendRequest({
      formKey: data.formKey,
      formName: data.formName,
      publishedFormUrl: data.publishedFormUrl ?? publishedFormUrl,
      mappings,
      customDynamicsFormId: data.customDynamicsFormId,
    });

    if (response?.success && !hasError) {
      setSubmitted(true);
    }

    return response;
  };

  return {
    isLoading,
    hasError,
    submitted,
    sendForm,
  };
};
